import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Icon,
  Spinner,
  Avatar,
  Link,
} from "@chakra-ui/react";
import { FaStar, FaGoogle, FaQuoteLeft } from "react-icons/fa";
import { useGoogleReviews } from "../../../hooks/useGoogleReviews";

const SOLAR_GOLD = "#C69214";
const NAVY = "#141C49";

interface Props {
  placeId: string;
  minRating?: number;
}

function StarRating({ rating }: { rating: number }) {
  return (
    <HStack spacing="1px">
      {[1, 2, 3, 4, 5].map((i) => (
        <Icon
          key={i}
          as={FaStar}
          color={i <= rating ? SOLAR_GOLD : "gray.200"}
          boxSize={3}
        />
      ))}
    </HStack>
  );
}

export function GoogleReviews({ placeId, minRating = 4 }: Props) {
  const { data, loading, error } = useGoogleReviews(placeId);

  if (loading) {
    return (
      <Box py={16} textAlign="center">
        <Spinner color={SOLAR_GOLD} size="lg" />
      </Box>
    );
  }

  if (error || !data) {
    return null;
  }

  const filtered = data.reviews
    .filter((r) => r.rating >= minRating && r.text)
    .slice(0, 6);

  if (filtered.length === 0) return null;

  return (
    <Box bg="gray.50" py={{ base: 16, md: 24 }}>
      <Container maxW="container.xl">
        <VStack spacing={4} mb={14} textAlign="center">
          <HStack spacing={2}>
            <Icon as={FaGoogle} color="#4285F4" boxSize={6} />
            <Text fontWeight="700" fontSize="sm" color="gray.500" textTransform="uppercase" letterSpacing="wider">
              Google Reviews
            </Text>
          </HStack>
          <HStack spacing={3}>
            <Heading size="2xl" color={NAVY} fontWeight="900">{data.rating.toFixed(1)}</Heading>
            <VStack align="start" spacing={0}>
              <HStack spacing="2px">
                {[...Array(5)].map((_, i) => (
                  <Icon key={i} as={FaStar} color={SOLAR_GOLD} boxSize={5} />
                ))}
              </HStack>
              <Text fontSize="sm" color="gray.500">{data.totalRatings} reviews</Text>
            </VStack>
          </HStack>
          <Heading size="xl" color={NAVY}>What Our Customers Say</Heading>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
          {filtered.map((review) => (
            <Box
              key={review.time}
              bg="white"
              borderRadius="2xl"
              boxShadow="md"
              p={8}
              border="1px solid"
              borderColor="gray.100"
              _hover={{ boxShadow: "xl", borderColor: SOLAR_GOLD }}
              transition="all 0.3s"
              display="flex"
              flexDirection="column"
            >
              <Icon as={FaQuoteLeft} color={SOLAR_GOLD} boxSize={5} mb={4} opacity={0.6} />
              <Text color="gray.700" fontSize="sm" lineHeight="1.8" fontStyle="italic" flex={1} mb={6}>
                "{review.text}"
              </Text>
              <HStack spacing={4} justify="space-between" align="flex-end">
                <HStack spacing={3}>
                  <Avatar
                    src={review.profile_photo_url}
                    name={review.author_name}
                    size="sm"
                    border="2px solid"
                    borderColor={SOLAR_GOLD}
                  />
                  <VStack align="start" spacing={0}>
                    <Link
                      href={review.author_url}
                      isExternal
                      fontWeight="700"
                      color={NAVY}
                      fontSize="sm"
                      _hover={{ color: SOLAR_GOLD }}
                    >
                      {review.author_name}
                    </Link>
                    <StarRating rating={review.rating} />
                  </VStack>
                </HStack>
                <Text fontSize="xs" color="gray.400">{review.relative_time_description}</Text>
              </HStack>
            </Box>
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
}
